import { useState } from 'react';
import './ui/styles.css';
import { lookupsApi, securityApi } from './api/services';
import { UiProvider, useAsync } from './ui/components';
import { CompanyProvider } from './ui/company';
import { AccessProvider, PERM, canWith } from './ui/access';
import { YearEndPage } from './pages/setup/YearEndPage';
import { SettingsPage } from './pages/setup/SettingsPage';
import { SecurityPage } from './pages/admin/SecurityPage';
import { AuditPage } from './pages/admin/AuditPage';
import { TaxPage } from './pages/reports/TaxPage';
import { FxPage } from './pages/setup/FxPage';
import { FixedAssetsPage } from './pages/management/FixedAssetsPage';
import { InventoryPage } from './pages/management/InventoryPage';
import { CostCentersPage } from './pages/management/CostCentersPage';
import { BudgetPage } from './pages/management/BudgetPage';
import { PayrollLegalPage } from './pages/payroll/PayrollLegalPage';
import { Link, matchRoute, useHashPath, type RouteDef } from './router';
import { DashboardPage } from './pages/DashboardPage';
import { AccountsPage } from './pages/setup/AccountsPage';
import { StructurePage } from './pages/setup/StructurePage';
import { PeriodsPage } from './pages/setup/PeriodsPage';
import { IntegrationPage } from './pages/setup/IntegrationPage';
import { TemplatesPage } from './pages/setup/TemplatesPage';
import { EntriesPage, EntryEditorPage } from './pages/entries/EntriesPage';
import { ReceivablesPage } from './pages/receivables/ReceivablesPage';
import { PayablesPage } from './pages/payables/PayablesPage';
import { BanksPage } from './pages/banks/BanksPage';
import { CashPage } from './pages/cash/CashPage';
import { PettyCashPage } from './pages/cash/PettyCashPage';
import { PayrollPage } from './pages/payroll/PayrollPage';
import { ReportsPage } from './pages/reports/ReportsPage';

interface NavItem { to: string; label: string; perm?: string }
const NAV: { group: string; items: NavItem[] }[] = [
  { group: 'General', items: [{ to: '/', label: 'Resumen', perm: PERM.view }, { to: '/entries', label: 'Asientos', perm: PERM.view }, { to: '/reports', label: 'Reportes', perm: PERM.reports }, { to: '/tax', label: 'Impuestos', perm: PERM.reports }] },
  { group: 'Operación', items: [{ to: '/receivables', label: 'Cuentas por cobrar', perm: PERM.arView }, { to: '/payables', label: 'Cuentas por pagar', perm: PERM.apView }, { to: '/banks', label: 'Bancos', perm: PERM.bankView }, { to: '/cash', label: 'Cajas', perm: PERM.cashView }, { to: '/petty-cash', label: 'Caja chica', perm: PERM.cashView }, { to: '/payroll', label: 'Planilla', perm: PERM.payrollView }, { to: '/payroll-legal', label: 'Parámetros de planilla', perm: PERM.payrollView }] },
  { group: 'Gestión', items: [{ to: '/inventory', label: 'Inventario', perm: PERM.view }, { to: '/fixed-assets', label: 'Activos fijos', perm: PERM.view }, { to: '/cost-centers', label: 'Centros de costo', perm: PERM.view }, { to: '/budget', label: 'Presupuesto', perm: PERM.reports }] },
  { group: 'Configuración', items: [{ to: '/accounts', label: 'Catálogo de cuentas', perm: PERM.view }, { to: '/structure', label: 'Niveles y clasificaciones', perm: PERM.view }, { to: '/templates', label: 'Plantillas', perm: PERM.view }, { to: '/settings', label: 'Parámetros contables', perm: PERM.view }, { to: '/periods', label: 'Períodos', perm: PERM.view }, { to: '/fx', label: 'Diferencial cambiario', perm: PERM.view }, { to: '/year-end', label: 'Cierre anual', perm: PERM.view }, { to: '/integration', label: 'Integración', perm: PERM.setup }] },
  { group: 'Administración', items: [{ to: '/security', label: 'Usuarios y roles', perm: PERM.securityAdmin }, { to: '/audit', label: 'Bitácora', perm: PERM.auditView }] },
];

const ROUTES: (RouteDef & { perm?: string })[] = [
  { pattern: '/', render: () => <DashboardPage />, perm: PERM.view },
  { pattern: '/entries', render: () => <EntriesPage />, perm: PERM.view },
  { pattern: '/entries/new', render: () => <EntryEditorPage />, perm: PERM.view },
  { pattern: '/entries/:code', render: (p) => <EntryEditorPage key={p.code} code={p.code} />, perm: PERM.view },
  { pattern: '/reports', render: () => <ReportsPage />, perm: PERM.reports },
  { pattern: '/receivables', render: () => <ReceivablesPage />, perm: PERM.arView },
  { pattern: '/payables', render: () => <PayablesPage />, perm: PERM.apView },
  { pattern: '/banks', render: () => <BanksPage />, perm: PERM.bankView },
  { pattern: '/cash', render: () => <CashPage />, perm: PERM.cashView },
  { pattern: '/petty-cash', render: () => <PettyCashPage />, perm: PERM.cashView },
  { pattern: '/payroll', render: () => <PayrollPage />, perm: PERM.payrollView },
  { pattern: '/payroll-legal', render: () => <PayrollLegalPage />, perm: PERM.payrollView },
  { pattern: '/accounts', render: () => <AccountsPage />, perm: PERM.view },
  { pattern: '/structure', render: () => <StructurePage />, perm: PERM.view },
  { pattern: '/templates', render: () => <TemplatesPage />, perm: PERM.view },
  { pattern: '/settings', render: () => <SettingsPage />, perm: PERM.view },
  { pattern: '/periods', render: () => <PeriodsPage />, perm: PERM.view },
  { pattern: '/year-end', render: () => <YearEndPage />, perm: PERM.view },
  { pattern: '/fx', render: () => <FxPage />, perm: PERM.view },
  { pattern: '/tax', render: () => <TaxPage />, perm: PERM.reports },
  { pattern: '/inventory', render: () => <InventoryPage />, perm: PERM.view },
  { pattern: '/fixed-assets', render: () => <FixedAssetsPage />, perm: PERM.view },
  { pattern: '/cost-centers', render: () => <CostCentersPage />, perm: PERM.view },
  { pattern: '/budget', render: () => <BudgetPage />, perm: PERM.reports },
  { pattern: '/integration', render: () => <IntegrationPage />, perm: PERM.setup },
  { pattern: '/security', render: () => <SecurityPage />, perm: PERM.securityAdmin },
  { pattern: '/audit', render: () => <AuditPage />, perm: PERM.auditView },
];

/**
 * Módulo contable completo. Montarlo dentro de la aplicación existente después de llamar configureAccountingApi().
 * Usa rutas con hash (#/entries, #/reports, ...) para no interferir con el enrutador del host.
 */
export function AccountingApp() {
  return (
    <UiProvider>
      <Shell />
    </UiProvider>
  );
}

function Shell() {
  const path = useHashPath();
  const [menuOpen, setMenuOpen] = useState(false);
  const company = useAsync(() => lookupsApi.company(), [], true, false);
  // Si la API no informa permisos (versión anterior), no se oculta nada: la API igual valida cada operación.
  const access = useAsync(() => securityApi.me().catch(() => null), [], true, false);
  const me = access.data ?? null;
  const allowedRoutes = ROUTES.map((r) => (canWith(me, r.perm) ? r : { ...r, render: () => <NoAccess /> }));
  const page = matchRoute(path, allowedRoutes) ?? <p>Página no encontrada. <Link to="/">Ir al resumen</Link></p>;
  const nav = NAV.map((g) => ({ ...g, items: g.items.filter((i) => canWith(me, i.perm)) })).filter((g) => g.items.length > 0);
  const active = (to: string) => (to === '/' ? path === '/' : path === to || path.startsWith(`${to}/`));
  return (
    <div className="acc-app">
      <aside className={`acc-nav${menuOpen ? ' acc-nav-open' : ''}`}>
        <div className="acc-brand">Contabilidad<small>{company.data ? `${company.data.company.name}${company.data.country ? ` · ${company.data.country.name}` : ''}` : ' '}</small></div>
        <nav onClick={() => setMenuOpen(false)}>
          {nav.map((g) => (
            <div key={g.group} className="acc-nav-group">
              <span>{g.group}</span>
              {g.items.map((i) => <Link key={i.to} to={i.to} className={active(i.to) ? 'acc-active' : undefined}>{i.label}</Link>)}
            </div>
          ))}
        </nav>
      </aside>
      <main className="acc-main">
        <button type="button" className="acc-menu-btn" onClick={() => setMenuOpen(!menuOpen)} aria-label="Menú">☰ Menú</button>
        {company.error ? <div className="acc-alert acc-alert-error">No se pudo identificar la empresa del usuario. Verifique la sesión.</div> : null}
        <CompanyProvider value={company.data ?? null}><AccessProvider value={me}>{access.loading ? null : page}</AccessProvider></CompanyProvider>
      </main>
    </div>
  );
}

function NoAccess() {
  return (
    <div className="acc-alert acc-alert-warn">
      Su usuario no tiene permiso para esta pantalla. Si lo necesita, pídale a quien administra los usuarios de la contabilidad
      de su empresa que le asigne un rol con ese permiso (Administración → Usuarios y roles).
    </div>
  );
}
